import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import config from '../../config';
import { getLoggedinUser } from '../../helpers/api_helper';
import './MasterSpotlight.css';

interface MasterSpotlightProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  id: string;
  type: 'user' | 'project' | 'task' | 'action';
  title: string;
  subtitle?: string;
  icon: string;
  action?: () => void;
  url?: string;
}

const MasterSpotlight: React.FC<MasterSpotlightProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showInput, setShowInput] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const currentUser = getLoggedinUser();


  // All available pages for navigation
  const allPages: SearchResult[] = useMemo(() => [
    // Main Dashboard
    { id: 'dashboard', type: 'action', title: 'Dashboard', subtitle: 'Hauptübersicht', icon: 'ri-dashboard-line', url: '/base' },
    
    // Projects
    { id: 'projects', type: 'action', title: 'Projekte', subtitle: 'Alle Projekte anzeigen', icon: 'ri-folder-line', url: '/projects' },
    { id: 'projects-overview', type: 'action', title: 'Projektübersicht', subtitle: 'Karten-Ansicht der Projekte', icon: 'ri-grid-line', url: '/projects/overview' },
    { id: 'projects-create', type: 'action', title: 'Neues Projekt', subtitle: 'Projekt erstellen', icon: 'ri-add-line', url: '/projects/create' },
    { id: 'projects-pitch', type: 'action', title: 'Pitch Please', subtitle: 'Projekt-Pitches', icon: 'ri-presentation-line', url: '/projects/pitch-please' },
    { id: 'projects-graveyard', type: 'action', title: 'Projekt-Friedhof', subtitle: 'Archivierte Projekte', icon: 'ri-archive-line', url: '/projects/graveyard' },
    
    // Calculations
    { id: 'calculations', type: 'action', title: 'Kalkulationen', subtitle: 'Alle Kalkulationen', icon: 'ri-calculator-line', url: '/calculations' },
    { id: 'calc-templates', type: 'action', title: 'Kalkulations-Vorlagen', subtitle: 'Vorlagen verwalten', icon: 'ri-file-copy-line', url: '/calculations-templates' },
    { id: 'calc-comments', type: 'action', title: 'Kalkulations-Kommentare', subtitle: 'Kommentare-Übersicht', icon: 'ri-discuss-line', url: '/calculation-comments' },
    
    // Invoices
    { id: 'invoices', type: 'action', title: 'Rechnungen', subtitle: 'Ausgangsrechnungen', icon: 'ri-file-text-line', url: '/invoices' },
    { id: 'invoices-new', type: 'action', title: 'Neue Rechnung', subtitle: 'Rechnung erstellen', icon: 'ri-add-circle-line', url: '/invoices/new' },
    { id: 'incoming-invoices', type: 'action', title: 'Eingangsrechnungen', subtitle: 'Lieferantenrechnungen', icon: 'ri-inbox-line', url: '/incoming-invoices' },
    { id: 'my-approvals', type: 'action', title: 'Meine Freigaben', subtitle: 'Zu genehmigende Dokumente', icon: 'ri-checkbox-line', url: '/my-approvals' },
    
    // Financial
    { id: 'bank-statements', type: 'action', title: 'Kontoauszüge', subtitle: 'Bank-Transaktionen', icon: 'ri-bank-line', url: '/bank-statements' },
    { id: 'monthly-report', type: 'action', title: 'Monatsabschluss', subtitle: 'Monatliche Berichte', icon: 'ri-bar-chart-line', url: '/monthly-report' },
    { id: 'reports', type: 'action', title: 'Berichte', subtitle: 'Finanzberichte', icon: 'ri-file-chart-line', url: '/reports' },
    { id: 'credit-cards', type: 'action', title: 'Kreditkarten', subtitle: 'Kreditkarten verwalten', icon: 'ri-bank-card-line', url: '/credit-cards' },
    { id: 'expense-reports', type: 'action', title: 'Spesenabrechnungen', subtitle: 'Spesen verwalten', icon: 'ri-receipt-line', url: '/expense-reports' },
    { id: 'cashbook', type: 'action', title: 'Kassenbuch', subtitle: 'Bargeldbewegungen', icon: 'ri-wallet-line', url: '/cashbook' },
    { id: 'subscriptions', type: 'action', title: 'Abonnements', subtitle: 'Wiederkehrende Zahlungen', icon: 'ri-repeat-line', url: '/subscriptions' },
    
    // Documents
    { id: 'document-capture', type: 'action', title: 'Belegerfassung', subtitle: 'Dokumente scannen', icon: 'ri-camera-line', url: '/document-capture' },
    { id: 'document-review', type: 'action', title: 'Belegprüfung', subtitle: 'Dokumente überprüfen', icon: 'ri-file-search-line', url: '/document-review' },
    { id: 'my-documents', type: 'action', title: 'Meine Dokumente', subtitle: 'Persönliche Dokumente', icon: 'ri-folder-user-line', url: '/my-documents' },
    { id: 'emails', type: 'action', title: 'E-Mails', subtitle: 'E-Mail Postfach', icon: 'ri-mail-line', url: '/emails' },
    { id: 'receipts', type: 'action', title: 'Belege', subtitle: 'Belege verwalten', icon: 'ri-file-list-line', url: '/receipts' },
    
    // Time Management
    { id: 'time-entries', type: 'action', title: 'Zeiterfassung', subtitle: 'Arbeitszeiten erfassen', icon: 'ri-time-line', url: '/time-entries' },
    { id: 'time-approvals', type: 'action', title: 'Zeit-Freigaben', subtitle: 'Zeiten genehmigen', icon: 'ri-check-double-line', url: '/time-entries/approvals' },
    { id: 'quick-time', type: 'action', title: 'Schnell-Erfassung', subtitle: 'Zeit schnell erfassen', icon: 'ri-speed-line', url: '/time-entries/quick' },
    { id: 'time-sankey', type: 'action', title: 'Zeit-Visualisierung', subtitle: 'Sankey-Diagramm', icon: 'ri-flow-chart', url: '/time-entries/sankey' },
    { id: 'timetracker', type: 'action', title: 'Time Tracker', subtitle: 'Live-Zeiterfassung', icon: 'ri-timer-line', url: '/timetracker' },
    { id: 'allin-erfassung', type: 'action', title: 'All-In Erfassung', subtitle: 'All-In Zeiten', icon: 'ri-24-hours-line', url: '/allin-erfassung' },
    
    // Tasks & Communication
    { id: 'tasks', type: 'action', title: 'Aufgaben', subtitle: 'Task Board', icon: 'ri-task-line', url: '/tasks' },
    { id: 'messages', type: 'action', title: 'Nachrichten', subtitle: 'Chat & Nachrichten', icon: 'ri-chat-3-line', url: '/messages' },
    { id: 'notifications', type: 'action', title: 'Benachrichtigungen', subtitle: 'Alle Benachrichtigungen', icon: 'ri-notification-3-line', url: '/notifications' },
    { id: 'notes', type: 'action', title: 'Notizen', subtitle: 'Persönliche Notizen', icon: 'ri-sticky-note-line', url: '/notes' },
    { id: 'wiki', type: 'action', title: 'Wiki', subtitle: 'Wissensdatenbank', icon: 'ri-book-2-line', url: '/wiki' },
    { id: 'calendar', type: 'action', title: 'Kalender', subtitle: 'Termine & Events', icon: 'ri-calendar-line', url: '/calendar' },
    
    // HR & Absences
    { id: 'absences-request', type: 'action', title: 'Urlaub beantragen', subtitle: 'Abwesenheitsantrag', icon: 'ri-calendar-check-line', url: '/absences/request' },
    { id: 'absences-calendar', type: 'action', title: 'Abwesenheitskalender', subtitle: 'Team-Übersicht', icon: 'ri-calendar-event-line', url: '/absences/calendar' },
    { id: 'vacation-balance', type: 'action', title: 'Urlaubssaldo', subtitle: 'Resturlaub anzeigen', icon: 'ri-sun-line', url: '/absences/vacation-balance' },
    { id: 'absences-requests', type: 'action', title: 'Abwesenheitsanträge', subtitle: 'Anträge verwalten', icon: 'ri-file-list-3-line', url: '/absences/requests' },
    
    // Administration
    { id: 'users', type: 'action', title: 'Benutzer', subtitle: 'Benutzerverwaltung', icon: 'ri-user-line', url: '/users' },
    { id: 'customers', type: 'action', title: 'Kunden', subtitle: 'Kundenverwaltung', icon: 'ri-team-line', url: '/customers' },
    { id: 'user-roles', type: 'action', title: 'Benutzerrollen', subtitle: 'Rollen & Rechte', icon: 'ri-shield-user-line', url: '/user-roles' },
    { id: 'fleet-management', type: 'action', title: 'Fuhrpark', subtitle: 'Fahrzeuge verwalten', icon: 'ri-car-line', url: '/fleet-management' },
    
    // Settings & Profile
    { id: 'profile', type: 'action', title: 'Mein Profil', subtitle: 'Persönliche Einstellungen', icon: 'ri-user-settings-line', url: '/profile' },
    { id: 'password-manager', type: 'action', title: 'Passwort-Manager', subtitle: 'Passwörter verwalten', icon: 'ri-lock-password-line', url: '/password-manager' },
    { id: 'news', type: 'action', title: 'Neuigkeiten', subtitle: 'Unternehmensnews', icon: 'ri-newspaper-line', url: '/news' },
    
    // Dev Tools (if user has access)
    { id: 'dev-database', type: 'action', title: 'Database Insights', subtitle: 'Datenbank-Tools', icon: 'ri-database-2-line', url: '/dev-tools/database-insights' },
    { id: 'dev-bugs', type: 'action', title: 'Bug Reports', subtitle: 'Fehlerberichte', icon: 'ri-bug-line', url: '/dev-tools/bug-reports' },
    { id: 'version-history', type: 'action', title: 'Versionshistorie', subtitle: 'App-Versionen', icon: 'ri-history-line', url: '/version-history' },
    
    // Estimates
    { id: 'estimates', type: 'action', title: 'Angebote', subtitle: 'Kostenvoranschläge', icon: 'ri-file-paper-line', url: '/estimates' },
    { id: 'estimates-create', type: 'action', title: 'Neues Angebot', subtitle: 'Angebot erstellen', icon: 'ri-add-box-line', url: '/estimates/create' },
    
    // Other
    { id: 'turnovers', type: 'action', title: 'Umsätze', subtitle: 'Umsatzübersicht', icon: 'ri-money-euro-circle-line', url: '/turnovers' },
    { id: 'payment-reminders', type: 'action', title: 'Mahnwesen', subtitle: 'Überfällige Rechnungen', icon: 'ri-alert-line', url: '/payment-reminders/overdue' },
  ], []);

  // Default actions when no query - show most used pages
  const defaultActions: SearchResult[] = useMemo(() => [
    allPages.find(p => p.id === 'dashboard')!,
    allPages.find(p => p.id === 'projects')!,
    allPages.find(p => p.id === 'tasks')!,
    allPages.find(p => p.id === 'messages')!,
    allPages.find(p => p.id === 'time-entries')!,
    allPages.find(p => p.id === 'invoices')!,
    allPages.find(p => p.id === 'document-capture')!,
    allPages.find(p => p.id === 'absences-calendar')!,
  ].filter(Boolean), [allPages]);

  // const overlayRef = useRef<HTMLDivElement>(null);

  // Initialize with default actions and focus when opened
  useEffect(() => {
    if (isOpen) {
      // Reset state
      setQuery('');
      setSelectedIndex(0);
      setLoading(false);
      setResults(defaultActions);
      setShowInput(false);
      
      // Show input after animation completes
      setTimeout(() => {
        setShowInput(true);
      }, 150);
    } else {
      setShowInput(false);
    }
  }, [isOpen, defaultActions]);
  
  // Focus management when input becomes visible
  useEffect(() => {
    if (showInput && isOpen) {
      // Focus as soon as input is shown
      const timer = setTimeout(() => {
        const input = document.getElementById('master-spotlight-search-input') as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 0);
      
      return () => clearTimeout(timer);
    }
  }, [showInput, isOpen]);

  // Reset selected index only when results array length changes to a different value
  const prevResultsLength = useRef(results.length);
  useEffect(() => {
    if (prevResultsLength.current !== results.length) {
      setSelectedIndex(0);
      prevResultsLength.current = results.length;
    }
  }, [results.length]);

  // Search function with intelligent prefixes
  const searchData = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults(defaultActions);
      return;
    }

    const results: SearchResult[] = [];
    let pageResults: SearchResult[] = [];
    let needsApiCall = false;

    try {
      // Check if user is logged in
      if (!currentUser || !currentUser.token) {
        setResults([]);
        return;
      }
      
      // Parse query for prefixes
      const cleanQuery = searchQuery.toLowerCase().trim();
      
      // First, search through all available pages (instant, no loading)
      pageResults = allPages.filter(page => 
        page.title.toLowerCase().includes(cleanQuery) ||
        (page.subtitle && page.subtitle.toLowerCase().includes(cleanQuery))
      );
      
      // Add matching pages to results (limit to 10)
      pageResults.slice(0, 10).forEach(page => {
        results.push(page);
      });
      
      // If we already have page results, show them immediately
      if (pageResults.length > 0) {
        setResults(results);
      }
      
      // Check if we need API calls
      needsApiCall = cleanQuery.startsWith('@') || cleanQuery.startsWith('/') || cleanQuery.startsWith('#');
      
      // Also search projects and tasks for normal queries (not just with prefixes)
      if (!needsApiCall && cleanQuery.length >= 2) {
        // Search projects in background without showing loading
        Promise.all([
          // Search projects
          fetch(`${config.api.API_URL}/projects?search=${encodeURIComponent(cleanQuery)}&pageSize=5`, {
            headers: {
              'Authorization': `Bearer ${currentUser.token}`,
              'Content-Type': 'application/json'
            }
          }),
          // Search tasks
          fetch(`${config.api.API_URL}/tasks?search=${encodeURIComponent(cleanQuery)}&pageSize=5`, {
            headers: {
              'Authorization': `Bearer ${currentUser.token}`,
              'Content-Type': 'application/json'
            }
          })
        ]).then(async ([projectResponse, taskResponse]) => {
          const apiResults: SearchResult[] = [];
          
          // Process project results
          if (projectResponse.ok) {
            const projectData = await projectResponse.json();
            projectData.data?.forEach((project: any) => {
              apiResults.push({
                id: `project-${project.id}`,
                type: 'project',
                title: `${project.num} - ${project.title}`,
                subtitle: 'Projekt öffnen',
                icon: 'ri-folder-line',
                url: `/projects/${project.id}`
              });
            });
          }
          
          // Process task results
          if (taskResponse.ok) {
            const taskData = await taskResponse.json();
            taskData.data?.forEach((task: any) => {
              apiResults.push({
                id: `task-${task.id}`,
                type: 'task',
                title: task.title,
                subtitle: task.project_title ? `Projekt: ${task.project_title}` : 'Aufgabe öffnen',
                icon: 'ri-task-line',
                url: `/tasks`
              });
            });
          }
          
          // Merge with existing results
          if (apiResults.length > 0) {
            setResults(prev => {
              // Combine page results with API results
              const combined = [...pageResults.slice(0, 5), ...apiResults];
              return combined.length > 0 ? combined : prev;
            });
          }
        }).catch(err => {
          console.error('Search API error:', err);
        });
      }
      
      // Only show loading for prefix searches
      if (needsApiCall) {
        setLoading(true);
      }
      
      // @username - Search users
      if (cleanQuery.startsWith('@')) {
        const username = cleanQuery.slice(1);
        if (username.length > 0) {
          const response = await fetch(`${config.api.API_URL}/users?search=${encodeURIComponent(username)}&pageSize=5`, {
            headers: {
              'Authorization': `Bearer ${currentUser.token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            data.data?.forEach((user: any) => {
              results.push({
                id: `user-${user.id}`,
                type: 'user',
                title: user.name || user.full_name,
                subtitle: user.email,
                icon: 'ri-user-line',
                url: `/messages`
              });
            });
          }
        }
      }
      
      // /project - Search projects
      else if (cleanQuery.startsWith('/')) {
        const projectName = cleanQuery.slice(1);
        if (projectName.length > 0) {
          const response = await fetch(`${config.api.API_URL}/projects?search=${encodeURIComponent(projectName)}&pageSize=5`, {
            headers: {
              'Authorization': `Bearer ${currentUser.token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            data.data?.forEach((project: any) => {
              results.push({
                id: `project-${project.id}`,
                type: 'project',
                title: `${project.num} - ${project.title}`,
                subtitle: `Projekt öffnen`,
                icon: 'ri-folder-line',
                url: `/projects/${project.id}`
              });
            });
          }
        }
      }
      
      // #task - Search tasks
      else if (cleanQuery.startsWith('#')) {
        const taskName = cleanQuery.slice(1);
        if (taskName.length > 0) {
          const response = await fetch(`${config.api.API_URL}/tasks?search=${encodeURIComponent(taskName)}&pageSize=5`, {
            headers: {
              'Authorization': `Bearer ${currentUser.token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            data.data?.forEach((task: any) => {
              results.push({
                id: `task-${task.id}`,
                type: 'task',
                title: task.title,
                subtitle: task.project_title ? `Projekt: ${task.project_title}` : 'Aufgabe öffnen',
                icon: 'ri-task-line',
                url: `/tasks`
              });
            });
          }
        }
      }
      
      // Skip general API search - we only do page navigation search now
      // Users should use @ / # prefixes for specific searches



    } catch (error) {
      console.error('Search error:', error);
      // Set empty results on error
      if (!results.length) {
        setResults([{
          id: 'no-results',
          type: 'action',
          title: 'Keine Ergebnisse gefunden',
          subtitle: 'Versuche einen anderen Suchbegriff oder nutze @ / # für spezifische Suche',
          icon: 'ri-search-line',
          action: () => {}
        }]);
      }
    }

    setLoading(false);
  }, [currentUser, defaultActions, allPages]);

  // Instant search for navigation, debounced for API calls
  useEffect(() => {
    // Check if this is an API search (with prefix)
    const needsApiCall = query.trim().startsWith('@') || query.trim().startsWith('/') || query.trim().startsWith('#');
    
    if (needsApiCall && query.trim().length > 1) {
      // Debounce API calls
      const timeoutId = setTimeout(() => {
        searchData(query);
      }, 150);
      return () => clearTimeout(timeoutId);
    } else {
      // Instant search for navigation
      searchData(query);
    }
  }, [query, searchData]);

  const handleClose = useCallback(() => {
    // Zuerst Parent schließen, damit Overlay sofort entfernt wird
    onClose();

    // Danach lokalen State zurücksetzen (nächstes Tick)
    setTimeout(() => {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setLoading(false);
    }, 0);
  }, [onClose]);

  const handleSelect = useCallback((result: SearchResult) => {
    
    if (result.action) {
      result.action();
    } else if (result.url) {
      navigate(result.url);
      handleClose();
    }
  }, [navigate, handleClose]);

  // Global keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const newIndex = selectedIndex < results.length - 1 ? selectedIndex + 1 : 0;
        setSelectedIndex(newIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const newIndex = selectedIndex > 0 ? selectedIndex - 1 : results.length - 1;
        setSelectedIndex(newIndex);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, results, handleClose, handleSelect]);


  const getTypeColor = (type: string) => {
    switch (type) {
      case 'user': return '#28a745';
      case 'project': return '#007bff';
      case 'task': return '#ffc107';
      case 'action': return '#6f42c1';
      default: return '#6c757d';
    }
  };

  // Check if component should render MasterSpotlight
  const shouldRenderMasterSpotlight = isOpen && currentUser && currentUser.token;

  return (
    <>

      {/* MasterSpotlight overlay */}
      {shouldRenderMasterSpotlight && (
        <div 
          className="master-spotlight-overlay" 
          onClick={handleClose}
        >
          {/* Search UI */}
            <div 
              ref={spotlightRef}
              className="master-spotlight" 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="master-spotlight-header">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  {showInput ? (
                    <input
                      id="master-spotlight-search-input"
                      ref={(el) => {
                        inputRef.current = el;
                        // Focus when element is mounted
                        if (el) {
                          setTimeout(() => {
                            el.focus();
                          }, 0);
                        }
                      }}
                      type="text"
                      className="search-input"
                      placeholder="Navigation & Suche... Tippe um Seiten zu finden oder @ / # für spezifische Suche"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={false}
                    />
                  ) : (
                    <div className="search-input" style={{ backgroundColor: '#f8f9fa' }}></div>
                  )}
                  {loading && <Spinner size="sm" className="search-spinner" />}
                </div>
              </div>

              <div className="master-spotlight-results">
                {results.map((result, index) => (
                  <div
                    key={`${result.id}-${index}-${selectedIndex}`}
                    className={`result-item ${index === selectedIndex ? 'selected' : ''}`}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="result-icon">
                      <i 
                        className={result.icon} 
                        style={{ color: getTypeColor(result.type) }}
                      ></i>
                    </div>
                    <div className="result-content">
                      <div className="result-title">{result.title}</div>
                      {result.subtitle && (
                        <div className="result-subtitle">{result.subtitle}</div>
                      )}
                    </div>
                    <div className="result-type">
                      {result.type}
                    </div>
                  </div>
                ))}
              </div>

              <div className="master-spotlight-footer">
                <div className="shortcuts-hint">
                  <span><kbd>↑</kbd><kbd>↓</kbd> navigieren</span>
                  <span><kbd>Enter</kbd> auswählen</span>
                  <span><kbd>Esc</kbd> schließen</span>
                </div>
                <div className="search-hints">
                  <span>Navigation: Seiten suchen | <code>@</code> User | <code>/</code> Projekte | <code>#</code> Tasks</span>
                </div>
              </div>
            </div>
        </div>
      )}
    </>
  );
};

export default MasterSpotlight;